import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError, shopLogoUrl } from "@/lib/api";
import { applyBrandColor, DEFAULT_BRAND_COLOR } from "@/components/BrandTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Store, CalendarDays, Wrench } from "lucide-react";
import { toast } from "sonner";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(params.get("expired") === "1" ? "Sua sessão expirou. Entre novamente para continuar." : "");
  const [shopPublic, setShopPublic] = useState(null); // null=idle, false=not-found, obj=found
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    if (!slug) { setShopPublic(null); return; }
    (async () => {
      try {
        const { data } = await api.get(`/shops/public/by-slug/${slug}`);
        setShopPublic(data);
      } catch { setShopPublic(false); }
    })();
  }, [slug]);

  // Brand color: use shop's when known, reset to default on unmount.
  useEffect(() => {
    applyBrandColor(shopPublic?.brand_color || DEFAULT_BRAND_COLOR);
    return () => applyBrandColor(DEFAULT_BRAND_COLOR);
  }, [shopPublic?.brand_color]);

  // Browser tab title: shop name on /o/{slug}, AutoOficina otherwise.
  useEffect(() => {
    const base = "AutoOficina";
    if (shopPublic?.name) document.title = `${shopPublic.name} — Entrar`;
    else document.title = base;
    return () => { document.title = base; };
  }, [shopPublic?.name]);

  useEffect(() => {
    if (user && user !== false) {
      const next = params.get("next");
      if (next && next.startsWith("/")) {
        navigate(next, { replace: true });
        return;
      }
      if (user.role === "super_admin") navigate("/admin", { replace: true });
      else if (user.role === "owner") navigate("/owner", { replace: true });
      else navigate("/cliente", { replace: true });
    }
  }, [user, navigate, params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, slug || null);
      toast.success("Bem-vindo(a) de volta!");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasShopLogo = !!(shopPublic?.id && shopPublic?.logo_path);
  const shopName = shopPublic?.name || "";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          {hasShopLogo ? (
            <img src={shopLogoUrl(shopPublic.id)} alt={shopName} data-testid="shop-logo"
                 className="mx-auto h-20 sm:h-24 w-auto drop-shadow-[0_0_30px_rgba(var(--c-brand-rgb)/0.35)]" />
          ) : shopName ? (
            <div data-testid="shop-name-mark"
                 className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl border border-card2-border bg-card2 shadow-[0_8px_28px_-10px_rgba(var(--c-brand-rgb)/0.45)]">
              <Store className="h-6 w-6" style={{ color: "var(--c-brand)" }} />
              <span className="font-display text-2xl font-black uppercase tracking-wide text-zinc-100">{shopName}</span>
            </div>
          ) : (
            <>
              <img src="/brand/autooficina-logo-white.png" alt="AutoOficina" data-testid="brand-logo"
                   className="mx-auto h-20 sm:h-24 w-auto drop-shadow-[0_0_30px_rgba(74,222,128,0.35)] hidden dark:block" />
              <img src="/brand/autooficina-logo.png" alt="AutoOficina"
                   className="mx-auto h-20 sm:h-24 w-auto drop-shadow-[0_0_30px_rgba(74,222,128,0.35)] dark:hidden" />
            </>
          )}
          {slug && shopPublic === false && (
            <p data-testid="shop-not-found" className="font-mono-caps text-xs text-red-400 mt-4">Oficina não encontrada</p>
          )}
          {hasShopLogo && (
            <p data-testid="shop-context-name" className="font-display text-base font-bold text-zinc-100 mt-4">
              {shopName}
            </p>
          )}
          <p className="font-mono-caps text-xs text-zinc-500 mt-2">
            {shopName ? "Acesso de clientes" : "Rede de oficinas conectada"}
          </p>
        </div>

        <div data-testid="login-card" className="rounded-2xl border border-card2-border bg-card2 backdrop-blur-md p-6 sm:p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-6">
            <ShieldCheck className="h-4 w-4" style={{ color: "var(--c-brand)" }} />
            <span className="text-xs font-mono-caps text-zinc-400">Acesso seguro</span>
          </div>
          <h2 className="font-display text-2xl font-bold uppercase text-zinc-100 mb-6">Entrar na conta</h2>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs font-mono-caps text-zinc-400">E-mail</Label>
              <Input id="email" data-testid="login-email-input" type="email" autoComplete="email" required
                     value={email} onChange={(e) => setEmail(e.target.value)}
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="voce@oficina.com" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono-caps text-zinc-400">Senha</Label>
              <Input id="password" data-testid="login-password-input" type="password" autoComplete="current-password" required
                     value={password} onChange={(e) => setPassword(e.target.value)}
                     className="mt-1 h-11 bg-zinc-900 border-zinc-800 text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-[var(--c-brand)]"
                     placeholder="••••••••" />
            </div>
            <div className="text-right">
              <button type="button" data-testid="forgot-password-btn" onClick={() => setForgotOpen(true)}
                      className="text-xs text-zinc-400 hover:underline underline-offset-2"
                      style={{ color: "var(--c-brand-neon)" }}>
                Esqueci minha senha
              </button>
            </div>
            {error && (<div data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900 rounded-md px-3 py-2">{error}</div>)}
            <Button data-testid="login-submit-btn" type="submit" disabled={loading}
                    style={{ background: "var(--c-brand)", color: "var(--c-brand-fg)" }}
                    className="w-full h-12 rounded-2xl font-bold uppercase tracking-wider shadow-[0_8px_28px_-10px_rgba(var(--c-brand-rgb)/0.55)] hover:brightness-110">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
            </Button>
          </form>
          <p className="text-xs text-zinc-500 mt-6 text-center">
            {shopName ? "Use o e-mail e a senha enviados pela oficina." : "Super administradores, oficinas e clientes utilizam o mesmo acesso."}
          </p>
          {!shopName && (
            <p className="text-xs text-zinc-500 mt-3 text-center">
              Não tem conta? <Link to="/cadastro" data-testid="login-to-signup" className="hover:underline" style={{ color: "var(--c-brand-neon)" }}>Teste grátis</Link>
            </p>
          )}
        </div>
        {slug && (
          <Link to={`/mecanico/${slug}`} data-testid="login-mechanic-btn"
                className="mt-4 flex items-center justify-center gap-2 h-12 rounded-2xl border border-card2-border bg-card2 text-zinc-100 font-bold uppercase tracking-wider text-sm hover:brightness-110">
            <Wrench className="h-4 w-4" style={{ color: "var(--c-brand)" }} /> Acesso do mecânico
          </Link>
        )}
        {slug && shopPublic?.booking_enabled && (
          <Link to={`/o/${slug}/agendar`} data-testid="login-booking-btn"
                className="mt-4 flex items-center justify-center gap-2 h-12 rounded-2xl border border-card2-border bg-card2 text-zinc-100 font-bold uppercase tracking-wider text-sm hover:brightness-110">
            <CalendarDays className="h-4 w-4" style={{ color: "var(--c-brand)" }} /> Agendar serviço
          </Link>
        )}
        <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} defaultEmail={email}
                              shopSlug={slug || null} shop={shopPublic || null} />
      </div>
      {shopName && (
        <p data-testid="powered-by" className="mt-10 text-[11px] font-mono-caps text-zinc-600 tracking-widest">
          feito com AutoOficina
        </p>
      )}
      <p data-testid="legal-links" className="mt-6 text-[11px] text-zinc-600">
        <Link to="/privacidade" data-testid="link-privacy" className="hover:text-zinc-400 transition-colors">Privacidade</Link>
        <span className="mx-2">·</span>
        <Link to="/termos" data-testid="link-terms" className="hover:text-zinc-400 transition-colors">Termos de Serviço</Link>
      </p>
    </div>
  );
}
